/**
 * Telling the browser that a capture arrived.
 *
 * Out of `src/lib/sync-broadcast.ts` on #402. The channel name and the event name were two of
 * #149's six cross-module contracts on a duplicated literal: this file wrote them at `:32,36` and
 * `EntryUpdateProvider.tsx` wrote them again at `:401,403`. Producer and consumer are both in this
 * directory now, so the constants are declared once and imported — which is why the contract lands
 * with the move rather than a commit before it.
 */

import { createClient } from '@supabase/supabase-js'

/** The channel a user's clients listen on for captures arriving from outside the browser. */
export function entryUpdatesChannel(userId: string): string {
  return `entry-updates:${userId}`
}

/** The one event that channel carries. */
export const ENTRY_CREATED_EVENT = 'entry-created'

/**
 * Broadcast that a new entry was created.
 * Connected clients listening to the automerge-sync channel will receive this
 * and can trigger a document refresh.
 *
 * This is a server-side utility - uses secret key (sb_secret_...) for admin access.
 */
export async function broadcastEntryCreated(
  userId: string,
  entryId: string,
  entryType: string,
) {
  // Use secret key for server-side broadcast
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const supabaseSecretKey = process.env.SUPABASE_SECRET_KEY

  if (!supabaseUrl || !supabaseSecretKey) {
    console.warn('Missing Supabase credentials for broadcast')
    return
  }

  const supabase = createClient(supabaseUrl, supabaseSecretKey)

  // Set the secret key as the Realtime auth token so httpSend includes
  // it in the Authorization header (required for sb_secret_ keys)
  await supabase.realtime.setAuth(supabaseSecretKey)

  // Use a dedicated channel for entry notifications
  // Clients subscribe to this and merge when they receive notifications
  const channel = supabase.channel(entryUpdatesChannel(userId))

  // Use httpSend for server-side broadcast (REST-based, no WebSocket needed in serverless)
  try {
    await channel.httpSend(ENTRY_CREATED_EVENT, {
      entryId,
      entryType,
      timestamp: Date.now(),
    })
  } catch (error) {
    // httpSend rejects on non-202 responses — log but don't throw
    console.error('[broadcast] httpSend failed:', error)
  }

  // Clean up channel after broadcast
  await supabase.removeChannel(channel)
}
